import {
  CustomEvent,
  dateFromDateString,
  DateString,
  Id,
  Store,
  toDateString,
  UpdateCustomEventParams,
  Visibility,
} from '@nextforce/api'
import { CheckBox, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { Item } from 'custom_events/items'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FlatList, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { BooleanRow } from 'shared/components/boolean_row'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { StoreChooser } from 'shared/components/store_chooser'
import { TextRow } from 'shared/components/text_row'
import { VisibilityChooser } from './visibility_chooser'

const MAX_DESCRIPTION_LENGTH = 280
interface Props {
  activeStore: Store | undefined
  stores?: Store[]
  selectedStores?: Store[]
  customEvent?: CustomEvent
  loading: boolean
  deleting: boolean
  onSubmit(): void
  onCustomEventParamsChanged(customEventData: UpdateCustomEventParams): void
  onDelete(id: Id): void
  setCanSaveEvent: React.Dispatch<React.SetStateAction<boolean>>
  onPressRecentChanges: () => void
}

const visiblityData: Visibility[] = [
  { role: 'ALL', titleKey: 'all' },
  { role: 'OBSERVER', titleKey: 'managersAndAbove' },
  { role: 'OBSERVER_MANAGER', titleKey: 'managementTeam' },
]

export function EditCustomEventView({
  activeStore,
  customEvent,
  stores,
  selectedStores,
  onCustomEventParamsChanged,
  loading,
  deleting,
  onSubmit,
  onDelete,
  setCanSaveEvent,
  onPressRecentChanges,
}: Props) {
  const styles = useStyles()

  const currentDateAtStore = dateFromDateString(
    toDateString(moment()),
    activeStore?.tz
  )

  const [startDate, setStartDate] = useState<DateString>(
    toDateString(
      customEvent?.startDate ? customEvent.startDate : currentDateAtStore
    )
  )

  const [endDate, setEndDate] = useState<DateString>(
    toDateString(
      customEvent?.endDate ? customEvent.endDate : currentDateAtStore
    )
  )

  const [allDay, setAllDay] = useState<boolean>(true)
  const [blockRto, setBlockRto] = useState<boolean>(
    customEvent?.blockRto ?? false
  )

  const [name, setName] = useState<string>(customEvent?.summary ?? '')
  const [description, setDescription] = useState<string>(
    customEvent?.description ?? ''
  )

  const [visibility, setVisibility] = useState<Visibility | undefined>(
    customEvent?.visibleToRoles
  )

  const [selectedStoresIds, setSelectedStoresIds] = useState<Id[] | undefined>(
    customEvent
      ? selectedStores?.map(s => s.id)
      : activeStore
      ? [activeStore.id]
      : undefined
  )

  const [deselectedStoreIds, setDeselectedStoreIds] = useState<
    Id[] | undefined
  >(
    stores
      ?.map(store => store.id)
      .filter(id => !selectedStoresIds?.includes(id))
  )

  const items = useMemo(() => {
    const summaryItem: Item = {
      type: 'summary',
      key: 'summary',
    }

    const descriptionItem: Item = {
      type: 'description',
      key: 'description',
    }
    const allDayItem: Item = {
      type: 'allDay',
      key: 'allDay',
    }

    const startDateItem: Item = {
      type: 'startDate',
      key: 'startDate',
    }

    const endDateItem: Item = {
      type: 'endDate',
      key: 'endDate',
    }

    const storeItem: Item = {
      type: 'stores',
      key: 'stores',
    }

    const visiblityItem: Item = {
      type: 'visiblity',
      key: 'visiblity',
    }

    const blockRtoItem: Item = {
      type: 'blockRto',
      key: 'blockRto',
    }

    const eventChangesItem: Item = { type: 'eventChanges', key: 'eventChanges' }

    return [
      allDayItem,
      summaryItem,
      descriptionItem,
      startDateItem,
      endDateItem,
      storeItem,
      visiblityItem,
      blockRtoItem,
      eventChangesItem,
    ]
  }, [])

  const onStartDateChange = useCallback((selectedDate: Date) => {
    setStartDate(toDateString(selectedDate))
  }, [])

  const onEndDateChange = useCallback((selectedDate: Date) => {
    setEndDate(toDateString(selectedDate))
  }, [])

  const onAllDayChanged = useCallback(() => {
    setAllDay(!allDay)
  }, [allDay])

  const onSelectStores = useCallback(
    (currentSelectedStores: Store[]) => {
      const selectedStoreIds = currentSelectedStores.map(s => s.id)

      const allStoreIds = stores?.map(s => s.id)

      setSelectedStoresIds(selectedStoreIds)
      setDeselectedStoreIds(
        allStoreIds?.filter(id => !selectedStoreIds.includes(id))
      )
    },
    [stores]
  )

  const canSave = useMemo(() => {
    if (!name.length) {
      return false
    }
    if (!visibility) {
      return false
    }
    if (!selectedStoresIds?.length || !selectedStoresIds) {
      return false
    }
    return true
  }, [name.length, selectedStoresIds, visibility])

  useEffect(() => {
    onCustomEventParamsChanged({
      summary: name,
      description: description,
      startDate: startDate,
      endDate: endDate,
      recurring: false,
      frequency: 'DAILY',
      deleted: false,
      visibleToRoles: visibility?.role!,
      eventType: 'CUSTOM_EVENT',
      storeIds: selectedStoresIds,
      allDay: allDay,
      blockRto: blockRto,
      selectedStoreIds: selectedStoresIds ?? [],
      deselectedStoreIds: deselectedStoreIds ?? [],
    })
    setCanSaveEvent(canSave)
  }, [
    allDay,
    blockRto,
    canSave,
    description,
    deselectedStoreIds,
    endDate,
    name,
    onCustomEventParamsChanged,
    selectedStoresIds,
    setCanSaveEvent,
    startDate,
    visibility?.role,
  ])

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      if (!stores) {
        return null
      }
      switch (item?.type) {
        case 'summary':
          return (
            <View style={{ paddingTop: 16 }}>
              <TextRow title={'Title'} />
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.textInput}
                  onChangeText={setName}
                  value={name}
                  placeholder={t('eventTitle')}
                />
              </View>
            </View>
          )
        case 'description':
          return (
            <View style={{ paddingTop: 16 }}>
              <TextRow title={'Description'} />
              <View style={styles.reasonContainer}>
                <TextInput
                  style={[styles.textInput, styles.description]}
                  onChangeText={setDescription}
                  value={description}
                  placeholder={t('eventDescription')}
                  multiline
                  maxLength={MAX_DESCRIPTION_LENGTH}
                />
              </View>
            </View>
          )
        case 'allDay':
          return (
            <View style={{ marginVertical: 5 }}>
              <BooleanRow
                title={t('allDay')}
                value={allDay}
                onToggle={onAllDayChanged}
                disabled={true}
              />
            </View>
          )
        case 'startDate':
          return (
            <DateTimeChooser
              title={t('startDate')}
              value={dateFromDateString(startDate)}
              mode="date"
              onChange={onStartDateChange}
            />
          )
        case 'endDate':
          return (
            <DateTimeChooser
              title={t('endDate')}
              value={dateFromDateString(endDate)}
              mode="date"
              onChange={onEndDateChange}
            />
          )

        case 'stores':
          return (
            <StoreChooser
              stores={stores}
              activeStore={activeStore}
              selectedStores={selectedStores}
              title={t('stores')}
              onChange={onSelectStores}
              required
              value={
                selectedStoresIds?.length
                  ? t('storeSelectedText', { count: selectedStoresIds.length })
                  : undefined
              }
            />
          )

        case 'visiblity':
          return (
            <VisibilityChooser
              title={t('visibility')}
              items={visiblityData}
              value={visibility}
              stores={stores.length ?? 0}
              required
              setVisiblity={setVisibility}
            />
          )

        case 'blockRto':
          return (
            <CheckBox
              checked={blockRto}
              title={t('blockRtoCustomEventText')}
              onPress={() => setBlockRto(!blockRto)}
              containerStyle={styles.blockRtoContainer}
              textStyle={styles.blockRtoText}
            />
          )

        case 'eventChanges':
          return (
            <PressableOpacity onPress={onPressRecentChanges}>
              <Text style={styles.eventChangeText}>{`${t(
                'changeHistory'
              )} >`}</Text>
            </PressableOpacity>
          )
        default:
          return null
      }
    },
    [
      stores,
      styles.inputContainer,
      styles.textInput,
      styles.reasonContainer,
      styles.description,
      styles.blockRtoContainer,
      styles.blockRtoText,
      styles.eventChangeText,
      name,
      description,
      allDay,
      onAllDayChanged,
      startDate,
      onStartDateChange,
      endDate,
      onEndDateChange,
      activeStore,
      selectedStores,
      onSelectStores,
      selectedStoresIds?.length,
      visibility,
      blockRto,
      onPressRecentChanges,
    ]
  )

  return (
    <>
      <FlatList style={styles.container} data={items} renderItem={renderItem} />
      <SafeAreaView edges={['left', 'right', 'bottom']}>
        <View style={styles.footer}>
          {customEvent ? (
            <RoundedButton
              onPress={() => onDelete(customEvent.id)}
              danger
              title={t('deleteEvent')}
              loading={deleting}
              buttonStyle={styles.buttonStyle}
            />
          ) : null}
        </View>
      </SafeAreaView>
    </>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.colors?.white,
    flex: 1,
    marginTop: 10,
  },
  textInput: {
    marginTop: 10,
    color: theme.colors?.black,
    backgroundColor: theme.colors?.white,
    textAlignVertical: 'top',
    padding: 10,
    borderBottomWidth: 2,
    borderBottomColor: theme.colors?.grey1,
  },
  description: {
    height: 100,
    marginBottom: 15,
  },
  footer: {
    padding: 20,
  },
  footerText: {
    color: theme.colors?.primary,
    fontFamily: font.bold,
    textAlign: 'center',
    marginBottom: 10,
  },
  reasonContainer: {
    paddingHorizontal: 20,
    backgroundColor: theme.colors.white,
  },
  label: {
    fontSize: 16,
    fontFamily: font.normal,
    paddingHorizontal: 20,
    marginHorizontal: 0,
    color: theme.colors?.black,
  },
  inputContainer: {
    paddingHorizontal: 20,
    backgroundColor: theme.colors?.white,
  },
  buttonStyle: {
    margin: 5,
  },
  blockRtoContainer: {
    maxWidth: 300,
    marginVertical: 10,
  },
  blockRtoText: {
    textAlign: 'justify',
    fontSize: 17,
  },
  eventChangeText: {
    fontSize: 14,
    textAlign: 'right',
    paddingHorizontal: 20,
    paddingVertical: 10,
    color: theme.colors?.grey3,
  },
}))
